import React from 'react';
import { Link } from 'react-router-dom';
import { Sun, BatteryCharging, BadgePercent, Droplets, BookOpen, ArrowRight, Clock, CalendarDays } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Seo, SITE_URL, SITE_NAME } from '../components/Seo';
import { BLOG_POSTS } from '../data/blogPosts';

export const CATEGORY_STYLES = {
  Solar: { gradient: 'from-amber-500 to-orange-600', icon: Sun },
  Battery: { gradient: 'from-blue-600 to-cyan-600', icon: BatteryCharging },
  Rebates: { gradient: 'from-emerald-600 to-teal-600', icon: BadgePercent },
  'Hot Water': { gradient: 'from-sky-600 to-blue-700', icon: Droplets },
  Guides: { gradient: 'from-gray-800 to-gray-900', icon: BookOpen },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

const BlogCard = ({ post, large }) => {
  const style = CATEGORY_STYLES[post.category] || CATEGORY_STYLES.Guides;
  const Icon = style.icon;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className={`group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all ${large ? 'sm:flex' : ''}`}
    >
      <div
        className={`relative ${large ? 'sm:w-1/2 min-h-[200px]' : 'h-44'} bg-gradient-to-br ${style.gradient} flex items-center justify-center overflow-hidden`}
      >
        {post.heroImage ? (
          <img src={post.heroImage} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <Icon className="w-14 h-14 text-white/90" />
        )}
        <span className="absolute top-3 left-3 text-[11px] font-bold uppercase tracking-wider text-white bg-black/30 backdrop-blur px-2.5 py-1 rounded-full">
          {post.category}
        </span>
      </div>
      <div className={`p-5 sm:p-6 ${large ? 'sm:w-1/2 sm:flex sm:flex-col sm:justify-center' : ''}`}>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(post.datePublished)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.readMinutes} min read</span>
        </div>
        <h3 className={`mt-2 font-black text-gray-900 leading-snug group-hover:text-cyo-orange transition-colors ${large ? 'text-2xl' : 'text-lg'}`}>
          {post.title}
        </h3>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed line-clamp-3">{post.excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-cyo-orange">
          Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
};

const BlogIndexPage = () => {
  const posts = [...BLOG_POSTS].sort((a, b) => new Date(b.datePublished) - new Date(a.datePublished));
  const [featured, ...rest] = posts;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `${SITE_NAME} Blog`,
    url: `${SITE_URL}/blog`,
    description: 'Guides and insights on solar, batteries, heat pumps, rebates and saving on energy in Australia.',
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `${SITE_URL}/blog/${p.slug}`,
      datePublished: p.datePublished,
      author: { '@type': 'Organization', name: SITE_NAME },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Solar, Battery & Energy Blog | CompareYourOptions.com.au"
        description="Practical guides on solar, batteries, heat pumps and rebates for Australian homes. Learn how to cut your power bills and compare your options."
        path="/blog"
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gray-950 text-white" data-testid="blog-index-page">
          <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16 text-center">
            <span className="inline-flex items-center gap-1.5 bg-cyo-orange/15 border border-cyo-orange/30 text-cyo-orange px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              <BookOpen className="w-3.5 h-3.5" /> The CYO Blog
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
              Energy savings, <span className="text-cyo-orange">made simple</span>
            </h1>
            <p className="mt-4 text-gray-300 max-w-2xl mx-auto text-sm sm:text-base">
              Clear, no-nonsense guides on solar, batteries, heat pumps and rebates — to help
              Australian homes make confident choices and cut power bills.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          {featured && (
            <div className="mb-8">
              <BlogCard post={featured} large />
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-cyo-orange to-orange-600 text-white py-12 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-black">Ready to see your numbers?</h2>
            <p className="mt-3 text-white/90 text-sm sm:text-base">
              Compare solar, battery and more for your home — free, in about 2 minutes.
            </p>
            <Link
              to="/compare"
              data-testid="blog-index-cta"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-white text-cyo-orange font-bold px-7 py-4 rounded-full shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Compare My Options <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogIndexPage;
